/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect, useCallback } from "react";
import { simpleFetch } from "../../restWrapper";
import "./NonprofitLinker.css"; // Create this CSS file later
import PropTypes from "prop-types";

function NonprofitLinker({ languageObj = {}, defaultPercentage = 1 }) {
  const [availableNonprofits, setAvailableNonprofits] = useState([]);
  const [linkedNonprofits, setLinkedNonprofits] = useState([]);
  const [selectedNonprofit, setSelectedNonprofit] = useState("");
  const [percentage, setPercentage] = useState(defaultPercentage); // Initialize with default
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [linkSuccess, setLinkSuccess] = useState("");

  const fetchAvailableNonprofits = useCallback(async () => {
    try {
      // Corrected API endpoint path
      const response = await simpleFetch(
        "/businesses/revenue-share/nonprofits/list",
        "GET",
      );
      // Check response.status instead of response.ok because simpleFetch returns a custom object
      if (response.status === 200) {
        // Access the body property of the custom response object
        setAvailableNonprofits(response.body || []);
      } else {
        setError("Failed to fetch available nonprofits.");
      }
    } catch (err) {
      setError("Error fetching available nonprofits.");
      // console.error(err);
    }
  }, []);

  const fetchLinkedNonprofits = useCallback(async () => {
    setIsLoading(true); // Set loading true when fetching linked nonprofits
    setError("");
    try {
      const response = await simpleFetch(
        "/businesses/revenue-share/nonprofits",
        "GET",
      );
      // Check response.status instead of response.ok because simpleFetch returns a custom object
      if (response.status === 200) {
        // Access the body property of the custom response object
        setLinkedNonprofits(response.body || []);
      } else {
        setError("Failed to fetch linked nonprofits.");
      }
    } catch (err) {
      setError("Error fetching linked nonprofits.");
      // console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch both lists on component mount
    const fetchData = async () => {
      setIsLoading(true);
      await fetchAvailableNonprofits();
      await fetchLinkedNonprofits();
      // setIsLoading(false); // Loading is set false within fetchLinkedNonprofits
    };
    fetchData();
  }, [fetchAvailableNonprofits, fetchLinkedNonprofits]);

  // Update local percentage state if defaultPercentage prop changes
  useEffect(() => {
    setPercentage(defaultPercentage);
  }, [defaultPercentage]);

  const handlePercentageChange = (event) => {
    const value = event.target.value;
    if (
      value === "" ||
      (/^\d*\.?\d*$/.test(value) &&
        parseFloat(value) >= 0 &&
        parseFloat(value) <= 100)
    ) {
      setPercentage(value);
    }
    setLinkError(""); // Clear errors on change
    setLinkSuccess("");
  };

  const handlePercentageBlur = () => {
    let finalPercentage = parseFloat(percentage);
    if (
      isNaN(finalPercentage) ||
      finalPercentage <= 0 ||
      finalPercentage > 100
    ) {
      finalPercentage = defaultPercentage; // Reset to default if invalid
    }
    setPercentage(finalPercentage);
  };

  const handleLinkNonprofit = async (event) => {
    event.preventDefault(); // Prevent default form submission
    setLinkError("");
    setLinkSuccess("");

    if (!selectedNonprofit) {
      setLinkError("Please select a nonprofit.");
      return;
    }

    let finalPercentage = parseFloat(percentage);
    if (
      isNaN(finalPercentage) ||
      finalPercentage <= 0 ||
      finalPercentage > 100
    ) {
      setLinkError("Invalid percentage. Must be between 0.01 and 100.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        nonprofitId: selectedNonprofit,
        percentage: finalPercentage,
      };
      const response = await simpleFetch(
        "/businesses/revenue-share/nonprofit",
        "POST",
        payload,
      );
      // Check response.status for success (200 or 201)
      if (response.status === 200 || response.status === 201) {
        setLinkSuccess("Nonprofit linked successfully!");
        setSelectedNonprofit(""); // Reset selection
        setPercentage(defaultPercentage); // Reset percentage to default
        await fetchLinkedNonprofits(); // Refresh the list of linked nonprofits
        setTimeout(() => setLinkSuccess(""), 3000); // Clear success message
      } else {
        // Access error message from response.body (check if body exists first)
        const errorMessage =
          response.body && response.body.message
            ? response.body.message
            : "Failed to link nonprofit.";
        setLinkError(errorMessage);
      }
    } catch (err) {
      setLinkError("An error occurred while linking the nonprofit.");
      // console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlinkNonprofit = async (revenueShareId) => {
    if (
      !window.confirm(
        languageObj.confirmUnlink ||
          "Are you sure you want to unlink this nonprofit?",
      )
    ) {
      return;
    }

    setIsLoading(true);
    setError(""); // Clear main error
    try {
      const response = await simpleFetch(
        `/businesses/revenue-share/nonprofit/${revenueShareId}`,
        "DELETE",
      );
      // Check response.status for success (200 or 204)
      if (response.status === 200 || response.status === 204) {
        setLinkSuccess("Nonprofit unlinked successfully!"); // Use linkSuccess for consistency
        await fetchLinkedNonprofits(); // Refresh the list
        setTimeout(() => setLinkSuccess(""), 3000);
      } else {
        // Access error message from response.body (check if body exists first)
        const errorMessage =
          response.body && response.body.message
            ? response.body.message
            : "Failed to unlink nonprofit.";
        setError(errorMessage); // Use main error for unlink failures
      }
    } catch (err) {
      setError("An error occurred while unlinking the nonprofit.");
      // console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="nonprofit-linker-container">
      <h4>
        {languageObj.linkNonprofitsTitle ||
          "Link Nonprofits for Revenue Sharing"}
      </h4>
      {error && <div className="linker-error main-error">{error}</div>}

      {/* Form to Link New Nonprofit */}
      <form onSubmit={handleLinkNonprofit} className="link-form">
        <h5>{languageObj.addNonprofitTitle || "Add Nonprofit Link"}</h5>
        {linkError && <div className="linker-error">{linkError}</div>}
        {linkSuccess && <div className="linker-success">{linkSuccess}</div>}

        <div className="form-group">
          <label htmlFor="nonprofitSelect">
            {languageObj.selectNonprofitLabel || "Select Nonprofit:"}
          </label>
          <select
            id="nonprofitSelect"
            value={selectedNonprofit}
            onChange={(e) => {
              setSelectedNonprofit(e.target.value);
              setLinkError("");
              setLinkSuccess("");
            }}
            required
            disabled={isLoading}
          >
            <option value="">
              {languageObj.selectOption || "-- Select --"}
            </option>
            {availableNonprofits.map((np) => (
              <option key={np.id} value={np.id}>
                {np.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="linkPercentage">
            {languageObj.donationPercentageLabel || "Donation Percentage (%):"}
          </label>
          <input
            id="linkPercentage"
            type="number"
            value={percentage}
            onChange={handlePercentageChange}
            onBlur={handlePercentageBlur}
            min="0.01" // Minimum donation
            max="100"
            step="0.01"
            required
            disabled={isLoading}
            className="percentage-input"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !selectedNonprofit}
          className="link-button"
        >
          {isLoading
            ? languageObj.linkingButton || "Linking..."
            : languageObj.linkButton || "Link Nonprofit"}
        </button>
      </form>

      {/* List of Linked Nonprofits */}
      <div className="linked-nonprofits-list">
        <h5>
          {languageObj.linkedNonprofitsTitle || "Currently Linked Nonprofits"}
        </h5>
        {isLoading && linkedNonprofits.length === 0 && (
          <div>Loading linked nonprofits...</div>
        )}
        {!isLoading && linkedNonprofits.length === 0 && (
          <p>
            {languageObj.noLinkedNonprofits ||
              "No nonprofits are currently linked for revenue sharing."}
          </p>
        )}
        {linkedNonprofits.length > 0 && (
          <ul>
            {linkedNonprofits.map((link) => (
              <li key={link.revenueShareId}>
                <span className="nonprofit-name">{link.name}</span>
                <span className="nonprofit-percentage">{link.percentage}%</span>
                <button
                  onClick={() => handleUnlinkNonprofit(link.revenueShareId)}
                  disabled={isLoading}
                  className="unlink-button"
                >
                  {languageObj.unlinkButton || "Unlink"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

NonprofitLinker.propTypes = {
  languageObj: PropTypes.object,
  defaultPercentage: PropTypes.number,
};

export default NonprofitLinker;
